import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Copy, Check, Share2, Download } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { usePricing } from "@/hooks/usePricing";

type Section = "mvp-overview" | "mvp-prompt" | "marketing" | "ads" | "launch-plan" | "domain" | "legal" | "payments" | "analytics" | "launch" | "tools";

const BuildWorkspace = () => {
  const { ideaId } = useParams<{ ideaId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeSection, setActiveSection] = useState<Section>("mvp-overview");
  const [copiedPrompt, setCopiedPrompt] = useState<string | null>(null);

  // Fetch idea details using the same query as IdeaDetail page
  const { data: idea, isLoading } = useQuery({
    queryKey: ["idea-detail", ideaId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ideas")
        .select("*")
        .eq("id", ideaId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!ideaId,
  });

  // Get pricing model for this idea
  const pricing = usePricing(idea);

  const copyPrompt = (prompt: string, label: string) => {
    navigator.clipboard.writeText(prompt);
    setCopiedPrompt(label);
    toast({
      title: "Copied to clipboard",
      description: "Prompt is ready to paste into your AI tool",
    });
    setTimeout(() => setCopiedPrompt(null), 2000);
  };

  const handleShareWorkspace = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    toast({
      title: "Link copied!",
      description: "Share this workspace with your team",
    });
  };

  const handleExportPrompts = () => {
    const allPrompts = `# Build Workspace: ${ideaTitle}

## MVP Overview Prompt

${generateMVPOverviewPrompt()}

---

## AI Build Prompt

${generateBuildPrompt()}

---

## Marketing Strategy Prompt

${generateMarketingPrompt()}

---

## Ad Creative Prompt

${generateAdScriptsPrompt()}

---

## Launch Checklist Prompt

${generateLaunchPrompt()}

---

*Exported from Build Workspace*
`;

    const blob = new Blob([allPrompts], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${ideaTitle.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-build-prompts.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast({
      title: "Prompts exported!",
      description: "Downloaded as markdown file",
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="h-12 w-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-muted-foreground">Loading build workspace...</p>
        </div>
      </div>
    );
  }

  if (!idea) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4 max-w-md">
          <h2 className="text-2xl font-bold">Idea Not Found</h2>
          <p className="text-muted-foreground">
            We couldn't load this idea. It may not exist or you don't have access to it.
          </p>
          <Button onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const ideaTitle = idea.title;
  const ideaDescription = idea.description || idea.tagline || "";
  const ideaTags = idea.tags || [];
  const ideaMarket = idea.niche || "";
  const ideaB2Type = "";

  // Navigation sections
  const sections: { id: Section; label: string }[] = [
    { id: "mvp-overview", label: "MVP Overview" },
    { id: "mvp-prompt", label: "MVP Build Prompt" },
    { id: "marketing", label: "Marketing Plan" },
    { id: "ads", label: "Ad Scripts" },
    { id: "launch-plan", label: "90-Day Launch Plan" },
    { id: "domain", label: "Domain & Email Setup" },
    { id: "legal", label: "Legal & LLC Formation" },
    { id: "payments", label: "Payments & Billing (Stripe)" },
    { id: "analytics", label: "Analytics & Metrics" },
    { id: "launch", label: "Launch & Operations" },
    { id: "tools", label: "Tool Recommendations" },
  ];

  // Generate prompts based on idea
  const generateMVPOverviewPrompt = () => {
    return `You are a senior product manager with experience shipping MVPs for successful startups.

Using the following startup idea, outline a realistic MVP in 3-5 core features that can be built and launched within 4-6 weeks:

**Idea:** ${ideaTitle}
**Description:** ${ideaDescription}
**Market:** ${ideaMarket}
**Tags:** ${ideaTags.join(", ")}

For each feature:
1. Describe what it does and why it's essential for the MVP
2. Estimate development complexity (low/medium/high)
3. Define success metrics

Then provide:
- Core user flow (3-5 steps)
- Technical requirements overview
- Must-have vs nice-to-have features
- Launch readiness checklist`;
  };

  const generateBuildPrompt = () => {
    return `You are an expert AI coding assistant. Build a complete Vite/React app for the following SaaS idea:

**Idea:** ${ideaTitle}
**Description:** ${ideaDescription}
**Market:** ${ideaMarket}

Create an app with these pages and features:
1. Landing page with clear value proposition and CTA
2. Authentication (sign up / log in) using Supabase Auth
3. Dashboard with main user workspace
4. Core feature pages (based on the idea description above)
5. Settings/profile page
6. Responsive design for mobile and desktop

Technical stack:
- Vite + React + TypeScript
- Tailwind CSS for styling
- Supabase for backend (auth + database)
- Shadcn UI components

Design requirements:
- Modern, clean interface
- Semantic color tokens from design system
- Smooth animations and transitions
- Mobile-first responsive design

Please implement the core MVP features that deliver the main value proposition of this idea.`;
  };

  const generateMarketingPrompt = () => {
    const channelHint = ideaB2Type?.includes("B2B") 
      ? "Focus on LinkedIn, cold email, and content marketing. Consider webinars and case studies."
      : "Focus on TikTok, Instagram, and community engagement. Consider influencer partnerships and viral content.";

    return `You are a growth marketing expert. Create a 30-day marketing plan for this startup:

**Idea:** ${ideaTitle}
**Description:** ${ideaDescription}
**Target Market:** ${ideaMarket}
**Business Type:** ${ideaB2Type}

${channelHint}

Please provide:

1. **Channel Strategy** (Week-by-week breakdown)
   - Primary channels and why
   - Budget allocation recommendations
   - Expected reach for each channel

2. **Content Calendar** (3 posts per week)
   - Post topics and angles
   - Format (video, carousel, long-form, etc.)
   - Call-to-action for each post

3. **Key Messages**
   - Main value proposition (1 sentence)
   - 3 supporting messages for different audience segments
   - Pain points to address

4. **Launch Sequence**
   - Pre-launch activities (days -7 to 0)
   - Launch day plan
   - Post-launch momentum tactics

5. **KPIs to Track**
   - Awareness metrics
   - Engagement metrics
   - Conversion metrics`;
  };

  const generateAdScriptsPrompt = () => {
    return `You are an expert in performance marketing and ad creative. Generate ad scripts for this startup:

**Idea:** ${ideaTitle}
**Description:** ${ideaDescription}
**Target Audience:** ${ideaMarket}

Please create:

**3 TikTok Ad Scripts** (15-30 seconds each)
For each script provide:
- Hook (first 3 seconds)
- Main content (problem → solution)
- Call-to-action
- On-screen text suggestions
- Storyboard notes (visual directions)

**3 Meta (Facebook/Instagram) Ad Angles**
For each angle provide:
- Primary text (125 characters max)
- Headline (40 characters max)
- Description
- Image/video concept
- Target audience hints

**1 Reddit Ad**
- Title
- Body text (conversational, native feel)
- Subreddit recommendations
- Comment strategy

Include:
- UGC (user-generated content) prompts if applicable
- A/B testing suggestions
- Targeting recommendations for each platform`;
  };

  const generateLaunchPlanPrompt = () => {
    return `You are a startup launch strategist. Create a detailed 90-day launch plan for this idea:

**Idea:** ${ideaTitle}
**Description:** ${ideaDescription}
**Market:** ${ideaMarket}

Break down the plan into three 30-day phases:

**Phase 1: Days 1-30 (Foundation)**
- Product development milestones
- Brand and legal setup
- Initial market research
- Team and resource allocation
- Technical infrastructure setup
- Budget planning and fundraising
- Early adopter identification

**Phase 2: Days 31-60 (Preparation)**
- Beta testing and feedback loops
- Marketing materials creation
- Partnership outreach
- Customer support setup
- Payment and billing integration
- Analytics and tracking setup
- Launch campaign planning

**Phase 3: Days 61-90 (Launch & Scale)**
- Soft launch to early adopters
- Public launch execution
- Press and media outreach
- Community engagement activities
- Performance monitoring and optimization
- User feedback collection
- Initial scaling strategies

For each phase, provide:
- Specific daily/weekly tasks
- Success metrics
- Key decisions to make
- Budget considerations
- Risk mitigation strategies`;
  };

  const generateDomainSetupPrompt = () => {
    return `You are a technical setup expert. Create a comprehensive domain and email setup guide for this startup:

**Idea:** ${ideaTitle}
**Description:** ${ideaDescription}

Provide detailed guidance on:

**Domain Selection & Registration**
- How to choose a domain name (SEO, branding, availability)
- Domain extensions (.com, .io, .app, etc.) and which to choose
- Where to register (Namecheap, Google Domains, Cloudflare)
- Domain privacy and security settings
- Expected costs

**DNS Configuration**
- A record setup for root domain
- CNAME for www subdomain
- MX records for email
- TXT records for verification (SPF, DKIM, DMARC)
- SSL/TLS certificate setup

**Professional Email Setup**
- Google Workspace vs Microsoft 365 vs Zoho Mail
- Email forwarding configuration
- Email signature best practices
- SPF, DKIM, and DMARC for deliverability
- Catch-all vs role-based email addresses

**Additional Setup**
- Subdomain strategy (app.domain.com, api.domain.com)
- CDN and performance optimization
- Email forwarding and aliases
- Monitoring and uptime alerts

Include step-by-step instructions and troubleshooting tips.`;
  };

  const generateLegalPrompt = () => {
    return `You are a startup legal advisor. Create a comprehensive legal setup guide for this idea:

**Idea:** ${ideaTitle}
**Description:** ${ideaDescription}
**Market:** ${ideaMarket}

**DISCLAIMER: This is educational information only. Always consult with a licensed attorney for legal advice specific to your situation.**

Provide guidance on:

**Business Structure Decision**
- LLC vs C-Corp vs S-Corp comparison
- Pros and cons for each structure
- Tax implications
- When to choose each option
- State registration considerations

**LLC Formation Process**
- Step-by-step formation guide
- Required documents and filings
- Registered agent requirements
- Operating agreement essentials
- EIN (Employer Identification Number) application
- State-specific requirements
- Estimated costs and timeline

**Essential Legal Documents**
- Terms of Service template outline
- Privacy Policy requirements (GDPR, CCPA)
- Cookie policy
- Acceptable Use Policy
- Refund and cancellation policy

**Compliance & Regulations**
- Industry-specific regulations for ${ideaMarket}
- Data protection requirements
- Payment processing compliance (PCI DSS)
- International considerations if applicable

**Intellectual Property**
- Trademark search and registration
- Copyright considerations
- Domain name protection
- Logo and brand asset protection

**Important Questions to Ask a Lawyer**
- What licenses or permits do I need?
- Should I form in Delaware or my home state?
- What contracts do I need with co-founders?
- How should I handle equity distribution?
- What insurance do I need?

Provide state-specific guidance if applicable and mention when professional legal counsel is essential.`;
  };

  const generatePaymentsPrompt = () => {
    return `You are a payments integration expert. Create a comprehensive Stripe setup and pricing guide for this SaaS:

**Idea:** ${ideaTitle}
**Description:** ${ideaDescription}
**Target Market:** ${ideaMarket}

Provide detailed guidance on:

**Pricing Model Strategy**
- Subscription vs one-time payment analysis
- Tiered pricing recommendations (Starter, Pro, Enterprise)
- Per-seat vs usage-based pricing
- Free trial strategy (7-day, 14-day, 30-day)
- Freemium vs paid-only approach
- Annual vs monthly pricing
- Pricing psychology and positioning

**Stripe Account Setup**
- Account registration and verification
- Test mode vs production mode
- Business details and tax information
- Payout schedule configuration
- Dispute settings
- Email notifications setup

**Technical Integration**
- Stripe Checkout vs Stripe Elements
- Subscription billing implementation
- Webhook configuration for events
- Customer portal integration
- Invoice and receipt automation
- Failed payment handling
- Dunning management (retry logic)

**Product & Price Configuration**
- Creating products in Stripe dashboard
- Setting up pricing tiers
- Metered billing setup (if usage-based)
- Coupons and promotional codes
- Trial period configuration

**Security & Compliance**
- PCI compliance requirements
- 3D Secure (SCA) for European customers
- Handling sensitive data
- Secure key management
- Fraud prevention with Radar

**Testing Checklist**
- Test card numbers for different scenarios
- Subscription lifecycle testing
- Webhook event testing
- Failed payment simulation
- Refund and cancellation testing

**Go-Live Checklist**
- Switch from test to production keys
- Update webhook endpoints
- Set up monitoring and alerts
- Configure tax collection (Stripe Tax)
- Enable payment methods for target markets

Include code snippets for common integration patterns and troubleshooting tips.`;
  };

  const generateAnalyticsPrompt = () => {
    return `You are a product analytics expert. Create a comprehensive analytics and metrics setup guide for this startup:

**Idea:** ${ideaTitle}
**Description:** ${ideaDescription}
**Market:** ${ideaMarket}

Provide guidance on:

**Core Metrics Framework**
- North Star Metric identification
- Key Performance Indicators (KPIs) for ${ideaMarket}
- Pirate Metrics (AARRR): Acquisition, Activation, Retention, Revenue, Referral
- Leading vs lagging indicators
- Vanity metrics to avoid

**Pre-Launch Metrics**
- Landing page conversion rate
- Email signup rate
- Waitlist growth rate
- Social media engagement
- Content performance
- Market validation signals

**Post-Launch Metrics**
- Daily/Monthly Active Users (DAU/MAU)
- User activation rate
- Feature adoption rate
- Customer acquisition cost (CAC)
- Lifetime value (LTV)
- Churn rate and retention cohorts
- Net Revenue Retention (NRR)
- Viral coefficient / K-factor

**Analytics Stack Recommendations**
- **Product Analytics**: PostHog (open source), Mixpanel, Amplitude
- **Web Analytics**: Plausible (privacy-friendly), Google Analytics 4
- **Session Recording**: PostHog, Hotjar, FullStory
- **Error Tracking**: Sentry, Bugsnag
- **Uptime Monitoring**: Better Stack, UptimeRobot
- **Business Metrics**: Baremetrics, ChartMogul (for SaaS)

**Event Tracking Strategy**
- User journey mapping
- Critical events to track
- Event naming conventions
- Custom properties and attributes
- Funnel analysis setup

**Dashboard Setup**
- Executive dashboard (weekly review)
- Product team dashboard (daily monitoring)
- Marketing dashboard (campaign performance)
- Customer success dashboard (health scores)

**Privacy & Compliance**
- GDPR considerations
- Cookie consent requirements
- Data retention policies
- User data export and deletion
- Anonymous vs identified tracking

**Implementation Checklist**
- Install tracking code
- Set up custom events
- Configure user properties
- Create key dashboards
- Set up alerts for critical metrics
- Enable goal tracking
- Implement A/B testing framework

**Reporting Cadence**
- Daily metrics review
- Weekly team sync on key metrics
- Monthly board reporting
- Quarterly goal setting

Include specific recommendations for ${ideaMarket} industry benchmarks and tools.`;
  };

  const generateLaunchPrompt = () => {
    return `You are a startup operations expert. Create a 60-90 day launch checklist for this idea:

**Idea:** ${ideaTitle}
**Description:** ${ideaDescription}

Provide a detailed checklist organized by category:

**Pre-Launch (30 days before)**
- Domain registration and setup
- Legal structure (LLC formation - optional but recommended)
- Brand assets (logo, colors, fonts)
- Social media profiles
- Email collection mechanism
- Analytics setup

**Launch Week (7 days)**
- Final QA and testing
- Payment processing setup (Stripe)
- Customer support channels
- Launch announcement preparation
- Influencer/partner outreach
- Product Hunt submission prep

**Post-Launch (60 days after)**
- Daily user monitoring
- Feature iteration based on feedback
- Content marketing cadence
- Community building activities
- Metrics review (weekly)
- First customer success stories

For each item:
- Estimated time required
- Dependencies
- Success criteria
- Tools/resources needed`;
  };

  // Render section content
  const renderSectionContent = () => {
    switch (activeSection) {
      case "mvp-overview":
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold mb-4">MVP Overview</h2>
              <p className="text-muted-foreground leading-relaxed">
                For <span className="font-semibold text-foreground">{ideaTitle}</span>, an effective MVP should focus on the core value proposition that solves the main problem for your target audience. 
                {ideaMarket && ` In the ${ideaMarket} space, `}
                your MVP needs to demonstrate clear differentiation and deliver immediate value. Start with 3-5 essential features that enable the primary user workflow, validate your solution with real users, and iterate based on feedback.
              </p>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  <span>MVP Planning Prompt</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyPrompt(generateMVPOverviewPrompt(), "mvp-overview")}
                  >
                    {copiedPrompt === "mvp-overview" ? (
                      <><Check className="h-4 w-4 mr-2" />Copied</>
                    ) : (
                      <><Copy className="h-4 w-4 mr-2" />Copy Prompt</>
                    )}
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="bg-muted p-4 rounded-lg text-sm whitespace-pre-wrap font-mono overflow-x-auto">
                  {generateMVPOverviewPrompt()}
                </pre>
              </CardContent>
            </Card>
          </div>
        );

      case "mvp-prompt":
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold mb-4">MVP Build Prompt</h2>
              <p className="text-muted-foreground leading-relaxed">
                Use this prompt with your preferred <strong>AI coding tool</strong> (like v0, Cursor, or any AI assistant) to start building your app immediately. 
                Simply copy this prompt, paste it into your AI tool, and let it generate a complete Vite/React application with authentication, database integration, and all the core pages you need to launch.
              </p>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  <span>AI Build Prompt</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyPrompt(generateBuildPrompt(), "mvp-prompt")}
                  >
                    {copiedPrompt === "mvp-prompt" ? (
                      <><Check className="h-4 w-4 mr-2" />Copied</>
                    ) : (
                      <><Copy className="h-4 w-4 mr-2" />Copy Prompt</>
                    )}
                  </Button>
                </CardTitle>
                <CardDescription>
                  Paste this into any AI coding tool to generate your complete application
                </CardDescription>
              </CardHeader>
              <CardContent>
                <pre className="bg-muted p-4 rounded-lg text-sm whitespace-pre-wrap font-mono overflow-x-auto">
                  {generateBuildPrompt()}
                </pre>
              </CardContent>
            </Card>

            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="pt-6">
                <p className="text-sm">
                  💡 <strong>Tip:</strong> After the AI generates your app, you can iterate by asking it to add features, modify the design, or integrate additional services.
                </p>
              </CardContent>
            </Card>
          </div>
        );

      case "marketing":
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold mb-4">Marketing Plan</h2>
              <p className="text-muted-foreground leading-relaxed">
                A solid marketing plan is essential for gaining traction. 
                {ideaB2Type?.includes("B2B") ? (
                  <> For B2B products like <span className="font-semibold text-foreground">{ideaTitle}</span>, focus on LinkedIn thought leadership, targeted cold outreach, and content that demonstrates expertise. Consider webinars, case studies, and partnerships with complementary tools.</>
                ) : (
                  <> For consumer products like <span className="font-semibold text-foreground">{ideaTitle}</span>, prioritize social media engagement, viral content creation, and community building. TikTok and Instagram Reels can provide explosive growth if executed well.</>
                )}
              </p>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  <span>Marketing Strategy Prompt</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyPrompt(generateMarketingPrompt(), "marketing")}
                  >
                    {copiedPrompt === "marketing" ? (
                      <><Check className="h-4 w-4 mr-2" />Copied</>
                    ) : (
                      <><Copy className="h-4 w-4 mr-2" />Copy Prompt</>
                    )}
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="bg-muted p-4 rounded-lg text-sm whitespace-pre-wrap font-mono overflow-x-auto">
                  {generateMarketingPrompt()}
                </pre>
              </CardContent>
            </Card>
          </div>
        );

      case "ads":
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold mb-4">Ad Scripts & Creative</h2>
              <p className="text-muted-foreground leading-relaxed">
                Great ads start with understanding your audience's pain points and presenting your solution in a compelling, authentic way. 
                This prompt will generate platform-specific ad scripts for <span className="font-semibold text-foreground">{ideaTitle}</span> that you can use as a foundation for your creative team or user-generated content.
              </p>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  <span>Ad Creative Prompt</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyPrompt(generateAdScriptsPrompt(), "ads")}
                  >
                    {copiedPrompt === "ads" ? (
                      <><Check className="h-4 w-4 mr-2" />Copied</>
                    ) : (
                      <><Copy className="h-4 w-4 mr-2" />Copy Prompt</>
                    )}
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="bg-muted p-4 rounded-lg text-sm whitespace-pre-wrap font-mono overflow-x-auto">
                  {generateAdScriptsPrompt()}
                </pre>
              </CardContent>
            </Card>
          </div>
        );

      case "launch-plan":
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold mb-4">90-Day Launch Plan</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                A strategic 90-day launch plan breaks down your journey from concept to market into three focused 30-day phases. 
                This structured approach for <span className="font-semibold text-foreground">{ideaTitle}</span> ensures you build momentum, 
                validate assumptions early, and launch with confidence.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Each phase has specific objectives: <strong>Foundation</strong> (building and preparing), 
                <strong>Preparation</strong> (testing and refining), and <strong>Launch & Scale</strong> (going live and iterating).
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Phase 1: Days 1-30</CardTitle>
                  <CardDescription>Foundation</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Finalize MVP feature set and technical architecture</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Complete brand identity (logo, colors, messaging)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Set up legal entity and business banking</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Build landing page and start email collection</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Conduct customer interviews (10-15 target users)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Set up development environment and CI/CD</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Draft budget and 6-month financial projections</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Identify and reach out to potential early adopters</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Phase 2: Days 31-60</CardTitle>
                  <CardDescription>Preparation</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Launch private beta with 20-50 early users</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Implement analytics and error tracking</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Create content library (blog posts, tutorials, videos)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Set up payment processing and subscription logic</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Build customer support workflow and documentation</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Conduct usability testing and iterate on feedback</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Prepare launch campaign materials and schedule</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Reach out to press, podcasts, and influencers</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Phase 3: Days 61-90</CardTitle>
                  <CardDescription>Launch & Scale</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Execute public launch (Product Hunt, Hacker News)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Monitor key metrics daily and fix critical issues</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Activate social media and content distribution</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Collect and showcase first customer testimonials</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Run initial ad campaigns (test with small budget)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Optimize onboarding flow based on user data</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Plan and prioritize next feature releases</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Review metrics and set goals for next quarter</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  <span>Detailed 90-Day Launch Plan Prompt</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyPrompt(generateLaunchPlanPrompt(), "launch-plan")}
                  >
                    {copiedPrompt === "launch-plan" ? (
                      <><Check className="h-4 w-4 mr-2" />Copied</>
                    ) : (
                      <><Copy className="h-4 w-4 mr-2" />Copy Prompt</>
                    )}
                  </Button>
                </CardTitle>
                <CardDescription>
                  Get a granular, day-by-day launch plan tailored to your idea
                </CardDescription>
              </CardHeader>
              <CardContent>
                <pre className="bg-muted p-4 rounded-lg text-sm whitespace-pre-wrap font-mono overflow-x-auto">
                  {generateLaunchPlanPrompt()}
                </pre>
              </CardContent>
            </Card>
          </div>
        );

      case "domain":
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold mb-4">Domain & Email Setup</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Your domain name is your digital identity—it's how customers find you and remember you. For <span className="font-semibold text-foreground">{ideaTitle}</span>, 
                choosing the right domain and setting up professional email is crucial for credibility and deliverability.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                A good domain should be memorable, easy to spell, and ideally match your brand name. While .com is still the gold standard, 
                alternatives like .io, .app, or .ai can work well for tech startups. Budget around $10-15/year for the domain and $6-12/month for professional email.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Setting up email properly with SPF, DKIM, and DMARC records ensures your messages reach inboxes instead of spam folders—critical for customer communication and marketing.
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Domain Selection</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">✓</span>
                      <span>Keep it short (under 15 characters if possible)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">✓</span>
                      <span>Avoid hyphens and numbers</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">✓</span>
                      <span>Check social media handle availability</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">✓</span>
                      <span>Consider .com first, then .io or .app</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">✓</span>
                      <span>Use Namecheap, Google Domains, or Cloudflare</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">✓</span>
                      <span>Enable domain privacy protection</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">DNS Configuration</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>A Record (@)</strong>: Points root domain to server IP</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>A Record (www)</strong>: Points www subdomain to server</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>MX Records</strong>: Routes email to email provider</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>TXT (SPF)</strong>: Prevents email spoofing</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>TXT (DKIM)</strong>: Email authentication signature</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>TXT (DMARC)</strong>: Email policy enforcement</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Professional Email Options</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>Google Workspace</strong>: $6/user/mo - Best overall</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>Microsoft 365</strong>: $6/user/mo - Enterprise features</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>Zoho Mail</strong>: Free for 5 users - Budget option</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>Fastmail</strong>: $5/mo - Privacy-focused</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Email Best Practices</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">✓</span>
                      <span>Use role-based emails: support@, hello@, team@</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">✓</span>
                      <span>Set up professional email signature</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">✓</span>
                      <span>Configure email forwarding for key addresses</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">✓</span>
                      <span>Test deliverability with mail-tester.com</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">✓</span>
                      <span>Set up catch-all for missed addresses</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  <span>Domain & Email Setup Prompt</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyPrompt(generateDomainSetupPrompt(), "domain")}
                  >
                    {copiedPrompt === "domain" ? (
                      <><Check className="h-4 w-4 mr-2" />Copied</>
                    ) : (
                      <><Copy className="h-4 w-4 mr-2" />Copy Prompt</>
                    )}
                  </Button>
                </CardTitle>
                <CardDescription>
                  Get step-by-step guidance for domain registration and email configuration
                </CardDescription>
              </CardHeader>
              <CardContent>
                <pre className="bg-muted p-4 rounded-lg text-sm whitespace-pre-wrap font-mono overflow-x-auto">
                  {generateDomainSetupPrompt()}
                </pre>
              </CardContent>
            </Card>
          </div>
        );

      case "legal":
        return (
          <div className="space-y-6">
            <Card className="border-amber-500/50 bg-amber-500/5">
              <CardContent className="pt-6">
                <p className="text-sm text-amber-900 dark:text-amber-200">
                  <strong>⚠️ Legal Disclaimer:</strong> This information is for educational purposes only and does not constitute legal advice. 
                  Always consult with a licensed attorney for guidance specific to your situation, jurisdiction, and business needs.
                </p>
              </CardContent>
            </Card>

            <div>
              <h2 className="text-2xl font-bold mb-4">Legal & LLC Formation</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Setting up the proper legal structure for <span className="font-semibold text-foreground">{ideaTitle}</span> protects you personally, 
                establishes credibility with customers and investors, and ensures compliance with regulations. Most startups begin with either an LLC (Limited Liability Company) 
                or a C-Corporation.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                An <strong>LLC</strong> is simpler, more flexible, and better for small teams or solo founders who want to minimize paperwork and taxes. 
                A <strong>C-Corp</strong> is standard for venture-backed startups planning to raise significant capital, as it allows for preferred stock and multiple share classes.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                For most early-stage SaaS products, starting with an LLC and converting to a C-Corp when raising institutional funding is a pragmatic approach.
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">LLC Formation Steps</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">1.</span>
                      <span><strong>Choose a business name</strong> and verify availability with your state</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">2.</span>
                      <span><strong>Select a registered agent</strong> (company or individual to receive legal documents)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">3.</span>
                      <span><strong>File Articles of Organization</strong> with your state ($50-$500 depending on state)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">4.</span>
                      <span><strong>Create an Operating Agreement</strong> (defines ownership and operating procedures)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">5.</span>
                      <span><strong>Obtain an EIN</strong> from the IRS (free, takes 10 minutes online)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">6.</span>
                      <span><strong>Open a business bank account</strong> using your EIN and formation documents</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">7.</span>
                      <span><strong>File annual reports</strong> and pay franchise taxes as required by your state</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">LLC vs C-Corp Comparison</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 text-sm">
                    <div>
                      <p className="font-semibold text-foreground mb-1">LLC Advantages:</p>
                      <ul className="space-y-1 ml-4">
                        <li>• Pass-through taxation (no double taxation)</li>
                        <li>• Simpler formation and maintenance</li>
                        <li>• Flexible profit distribution</li>
                        <li>• Less paperwork and lower costs</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold text-foreground mb-1">C-Corp Advantages:</p>
                      <ul className="space-y-1 ml-4">
                        <li>• Easier to raise venture capital</li>
                        <li>• Preferred stock and option pools</li>
                        <li>• Better for scaling and going public</li>
                        <li>• Certain tax benefits (QSBS exemption)</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Essential Legal Documents</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>Terms of Service</strong>: User rights and responsibilities</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>Privacy Policy</strong>: How you collect and use data (GDPR/CCPA)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>Cookie Policy</strong>: Disclosure of tracking technologies</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>Refund Policy</strong>: Cancellation and money-back terms</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>SLA (Service Level Agreement)</strong>: Uptime and support commitments</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>Acceptable Use Policy</strong>: Prohibited activities on your platform</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Questions for Your Lawyer</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">?</span>
                      <span>Should I form in Delaware or my home state?</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">?</span>
                      <span>What licenses or permits do I need for {ideaMarket}?</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">?</span>
                      <span>How should equity be distributed among co-founders?</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">?</span>
                      <span>What insurance coverage do I need?</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">?</span>
                      <span>How do I handle international sales and taxes?</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">?</span>
                      <span>Should I file for a trademark now or later?</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  <span>Legal Setup Guidance Prompt</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyPrompt(generateLegalPrompt(), "legal")}
                  >
                    {copiedPrompt === "legal" ? (
                      <><Check className="h-4 w-4 mr-2" />Copied</>
                    ) : (
                      <><Copy className="h-4 w-4 mr-2" />Copy Prompt</>
                    )}
                  </Button>
                </CardTitle>
                <CardDescription>
                  Get comprehensive legal formation guidance (remember to consult a lawyer)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <pre className="bg-muted p-4 rounded-lg text-sm whitespace-pre-wrap font-mono overflow-x-auto">
                  {generateLegalPrompt()}
                </pre>
              </CardContent>
            </Card>
          </div>
        );

      case "payments":
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold mb-4">Payments & Billing (Stripe)</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Getting payments right is critical for <span className="font-semibold text-foreground">{ideaTitle}</span>. 
                Stripe is the industry standard for SaaS billing, offering subscriptions, metered billing, invoicing, and a complete customer portal. 
                With Stripe, you can accept credit cards, ACH, and international payment methods while staying PCI compliant.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Your pricing model directly impacts growth: too high and you limit adoption, too low and you undervalue your solution. 
                Most SaaS products use tiered subscription pricing (Starter, Pro, Enterprise) with a 14-day free trial to lower friction. 
                Offering annual plans at a discount (e.g., 2 months free) improves cash flow and retention.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Stripe Checkout provides a hosted payment page in minutes, while Stripe Elements gives you full UI control. 
                Webhooks let you automate subscription events (renewals, failures, cancellations) to keep your app in sync with billing status.
              </p>
            </div>

            <div>
              <p className="text-sm text-muted-foreground mb-6">
                These suggested tiers are based on this idea's pricing from the main page.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                {pricing.tiers.map((tier, i) => (
                  <Card key={i} className={`border-border/50 hover:shadow-lg transition-shadow ${i === 1 ? 'border-primary/50' : ''}`}>
                    <CardContent className="p-6 text-center">
                      <div className="text-lg font-bold mb-1">
                        {tier.name}
                      </div>
                      <div className="text-2xl font-bold text-primary mb-3">
                        {tier.price}
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {tier.shortTagline}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Pricing Model Considerations</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>Tiered Pricing</strong>: 3 plans (adjust based on your market)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>Free Trial</strong>: 7-14 days, no credit card required</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>Annual Discount</strong>: 15-20% off (improves retention)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>Usage-Based</strong>: Per-seat, API calls, or storage limits</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>Value Metric</strong>: Align pricing with customer value delivered</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Stripe Account Setup</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">1.</span>
                      <span>Create Stripe account at stripe.com/register</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">2.</span>
                      <span>Complete business verification (EIN, bank account)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">3.</span>
                      <span>Get API keys (test mode and live mode)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">4.</span>
                      <span>Set up payout schedule (daily, weekly, monthly)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">5.</span>
                      <span>Configure email receipts and branding</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">6.</span>
                      <span>Enable Stripe Radar for fraud protection</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Integration Checklist</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">✓</span>
                      <span>Create products and prices in Stripe dashboard</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">✓</span>
                      <span>Implement Stripe Checkout or Elements for payments</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">✓</span>
                      <span>Set up webhooks for subscription events</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">✓</span>
                      <span>Build customer portal for self-service billing</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">✓</span>
                      <span>Handle failed payments with email retry logic</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">✓</span>
                      <span>Test with Stripe test cards (4242 4242 4242 4242)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">✓</span>
                      <span>Enable 3D Secure for European customers (SCA)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">✓</span>
                      <span>Switch to live keys and go production</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Key Webhook Events</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>customer.subscription.created</strong>: New subscription</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>customer.subscription.updated</strong>: Plan change</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>customer.subscription.deleted</strong>: Cancellation</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>invoice.payment_succeeded</strong>: Successful payment</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>invoice.payment_failed</strong>: Failed payment (retry)</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  <span>Stripe Integration Prompt</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyPrompt(generatePaymentsPrompt(), "payments")}
                  >
                    {copiedPrompt === "payments" ? (
                      <><Check className="h-4 w-4 mr-2" />Copied</>
                    ) : (
                      <><Copy className="h-4 w-4 mr-2" />Copy Prompt</>
                    )}
                  </Button>
                </CardTitle>
                <CardDescription>
                  Get detailed Stripe setup and pricing strategy guidance
                </CardDescription>
              </CardHeader>
              <CardContent>
                <pre className="bg-muted p-4 rounded-lg text-sm whitespace-pre-wrap font-mono overflow-x-auto">
                  {generatePaymentsPrompt()}
                </pre>
              </CardContent>
            </Card>

            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="pt-6">
                <p className="text-sm">
                  💡 <strong>Pro Tip:</strong> Start with Stripe Checkout for the fastest implementation, then migrate to Stripe Elements 
                  if you need custom UI. Always test in test mode before going live, and monitor failed payments closely in the first 30 days.
                </p>
              </CardContent>
            </Card>
          </div>
        );

      case "analytics":
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold mb-4">Analytics & Metrics</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Without analytics, you're flying blind. For <span className="font-semibold text-foreground">{ideaTitle}</span>, 
                tracking the right metrics helps you understand user behavior, identify bottlenecks, and make data-driven decisions. 
                Focus on actionable metrics—numbers that directly inform what to build, fix, or optimize next.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Your <strong>North Star Metric</strong> is the single number that best captures the core value you deliver to customers. 
                For a SaaS product, this might be "active projects created" or "weekly active users." This metric should grow as your product succeeds 
                and aligns with revenue growth.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Use the AARRR framework (Acquisition, Activation, Retention, Revenue, Referral) to organize your metrics. 
                Pre-launch, focus on landing page conversion and email signups. Post-launch, monitor activation rate (users who complete key actions) 
                and retention (users who return after 7 and 30 days).
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Essential Pre-Launch Metrics</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>Landing page conversion</strong>: Visitors → email signups</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>Waitlist growth rate</strong>: Daily/weekly new signups</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>Traffic sources</strong>: Where visitors are coming from</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>Social engagement</strong>: Shares, comments, reactions</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>Beta interest</strong>: Users requesting early access</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Critical Post-Launch Metrics</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>Daily/Monthly Active Users</strong>: DAU, MAU, DAU/MAU ratio</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>Activation rate</strong>: % completing onboarding or "aha moment"</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>Retention cohorts</strong>: D1, D7, D30 retention curves</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>Churn rate</strong>: Monthly customer and revenue churn</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>Net Revenue Retention</strong>: Expansion minus churn</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>CAC & LTV</strong>: Customer acquisition cost vs lifetime value</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Recommended Analytics Stack</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">📊</span>
                      <span><strong>PostHog</strong>: All-in-one product analytics (open source)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">📊</span>
                      <span><strong>Plausible</strong>: Privacy-friendly web analytics</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">📊</span>
                      <span><strong>Mixpanel</strong>: User behavior and funnel analysis</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">🐛</span>
                      <span><strong>Sentry</strong>: Error tracking and performance monitoring</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">📈</span>
                      <span><strong>Baremetrics</strong>: SaaS-specific revenue metrics</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">🔄</span>
                      <span><strong>Better Stack</strong>: Uptime monitoring and logs</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Events to Track</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>Sign up completed</strong>: New account created</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>Onboarding completed</strong>: User finished setup</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>First core action</strong>: Key feature used for first time</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>Subscription upgraded</strong>: User moved to paid plan</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>Feature used</strong>: Track adoption of each feature</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span><strong>User invited teammate</strong>: Referral or team expansion</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  <span>Analytics Setup Prompt</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyPrompt(generateAnalyticsPrompt(), "analytics")}
                  >
                    {copiedPrompt === "analytics" ? (
                      <><Check className="h-4 w-4 mr-2" />Copied</>
                    ) : (
                      <><Copy className="h-4 w-4 mr-2" />Copy Prompt</>
                    )}
                  </Button>
                </CardTitle>
                <CardDescription>
                  Get comprehensive analytics strategy and tool recommendations
                </CardDescription>
              </CardHeader>
              <CardContent>
                <pre className="bg-muted p-4 rounded-lg text-sm whitespace-pre-wrap font-mono overflow-x-auto">
                  {generateAnalyticsPrompt()}
                </pre>
              </CardContent>
            </Card>

            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="pt-6">
                <p className="text-sm">
                  💡 <strong>Quick Win:</strong> Install PostHog for free—it combines product analytics, session recording, and feature flags in one tool. 
                  Track your activation event (e.g., "First project created") from day one, then build dashboards around that.
                </p>
              </CardContent>
            </Card>
          </div>
        );

      case "launch":
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold mb-4">Launch & Operations</h2>
              <p className="text-muted-foreground leading-relaxed">
                A successful launch requires careful planning and execution across multiple dimensions: legal setup, technical infrastructure, marketing preparation, and post-launch support. 
                This checklist ensures you don't miss critical steps when bringing <span className="font-semibold text-foreground">{ideaTitle}</span> to market.
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2 mb-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Essential Pre-Launch</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Domain registration and email setup</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>LLC formation (optional but recommended)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Stripe or payment processing setup</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Analytics (PostHog, Plausible, or Google Analytics)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Customer support system (Intercom, plain.com, or email)</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Launch Week</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Final QA across all devices</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Product Hunt submission preparation</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Social media announcement posts</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Email list warm-up (if you have one)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Monitoring and support readiness</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  <span>Complete Launch Checklist Prompt</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyPrompt(generateLaunchPrompt(), "launch")}
                  >
                    {copiedPrompt === "launch" ? (
                      <><Check className="h-4 w-4 mr-2" />Copied</>
                    ) : (
                      <><Copy className="h-4 w-4 mr-2" />Copy Prompt</>
                    )}
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="bg-muted p-4 rounded-lg text-sm whitespace-pre-wrap font-mono overflow-x-auto">
                  {generateLaunchPrompt()}
                </pre>
              </CardContent>
            </Card>
          </div>
        );

      case "tools":
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold mb-4">Tool Recommendations</h2>
              <p className="text-muted-foreground leading-relaxed">
                Here's a curated list of tools organized by purpose. Most of these offer generous free tiers to help you launch without upfront costs.
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Building & Hosting</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <ToolItem 
                    name="Lovable" 
                    description="AI-powered app builder for Vite/React apps"
                    badge="AI Builder"
                  />
                  <ToolItem 
                    name="Replit" 
                    description="Cloud IDE with AI assistance"
                    badge="Free tier"
                  />
                  <ToolItem 
                    name="Vercel" 
                    description="Best-in-class deployment for Next.js and React"
                    badge="Free tier"
                  />
                  <ToolItem 
                    name="Netlify" 
                    description="Simple deployment with excellent DX"
                    badge="Free tier"
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Database & Auth</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <ToolItem 
                    name="Supabase" 
                    description="PostgreSQL database with authentication and real-time"
                    badge="Generous free tier"
                  />
                  <ToolItem 
                    name="Firebase" 
                    description="Google's backend-as-a-service platform"
                    badge="Free tier"
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Payments</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <ToolItem 
                    name="Stripe" 
                    description="Industry standard payment processing"
                    badge="Pay per transaction"
                  />
                  <ToolItem 
                    name="Lemon Squeezy" 
                    description="Merchant of record, handles taxes for you"
                    badge="Simpler setup"
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Email & Communications</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <ToolItem 
                    name="Resend" 
                    description="Developer-first transactional email"
                    badge="Free tier"
                  />
                  <ToolItem 
                    name="MailerLite" 
                    description="Email marketing for up to 1,000 subscribers"
                    badge="Free tier"
                  />
                  <ToolItem 
                    name="SendGrid" 
                    description="Established email delivery platform"
                    badge="Free tier"
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Analytics</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <ToolItem 
                    name="PostHog" 
                    description="Open source product analytics"
                    badge="Free tier"
                  />
                  <ToolItem 
                    name="Plausible" 
                    description="Privacy-friendly, simple analytics"
                    badge="Trial available"
                  />
                  <ToolItem 
                    name="Mixpanel" 
                    description="Advanced product analytics"
                    badge="Free tier"
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Monitoring & Observability</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <ToolItem 
                    name="Better Stack" 
                    description="Logs and uptime monitoring"
                    badge="Free tier"
                  />
                  <ToolItem 
                    name="Sentry" 
                    description="Error tracking and performance monitoring"
                    badge="Free tier"
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">AI Development Tools</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <ToolItem 
                    name="v0.dev" 
                    description="Vercel's AI UI component generator"
                    badge="AI powered"
                  />
                  <ToolItem 
                    name="Cursor" 
                    description="AI-powered code editor"
                    badge="AI coding"
                  />
                  <ToolItem 
                    name="Claude" 
                    description="Anthropic's AI assistant for complex tasks"
                    badge="LLM"
                  />
                  <ToolItem 
                    name="ChatGPT" 
                    description="OpenAI's conversational AI"
                    badge="LLM"
                  />
                  <ToolItem 
                    name="Gemini" 
                    description="Google's multimodal AI"
                    badge="LLM"
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Helmet>
        <title>Build {ideaTitle} | Build Workspace</title>
        <meta name="description" content={`Build plan and prompts for ${ideaTitle}`} />
      </Helmet>

      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8 max-w-7xl">
          {/* Header */}
          <div className="mb-8 space-y-4">
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleShareWorkspace}
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  Share Workspace
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportPrompts}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export All Prompts
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <h1 className="text-4xl font-bold">
                {ideaTitle}
              </h1>
              {ideaDescription && (
                <p className="text-lg text-muted-foreground">
                  {ideaDescription}
                </p>
              )}
              <div className="flex flex-wrap gap-2 pt-2">
                {ideaTags.slice(0, 5).map((tag: string, idx: number) => (
                  <Badge key={idx} variant="secondary">
                    {tag}
                  </Badge>
                ))}
                {ideaMarket && (
                  <Badge variant="outline">{ideaMarket}</Badge>
                )}
              </div>
            </div>
          </div>

          {/* Two-column layout */}
          <div className="flex gap-8">
            {/* Left sidebar navigation */}
            <aside className="w-64 shrink-0">
              <div className="sticky top-4">
                <div className="space-y-1">
                  {sections.map((section) => (
                    <Button
                      key={section.id}
                      variant={activeSection === section.id ? "secondary" : "ghost"}
                      className="w-full justify-start"
                      onClick={() => setActiveSection(section.id)}
                    >
                      {section.label}
                    </Button>
                  ))}
                </div>
              </div>
            </aside>

            {/* Main content area */}
            <main className="flex-1 min-w-0">
              {renderSectionContent()}
            </main>
          </div>
        </div>
      </div>
    </>
  );
};

// Helper component for tool items
const ToolItem = ({ name, description, badge }: { name: string; description: string; badge: string }) => (
  <div className="flex items-start justify-between gap-3">
    <div className="flex-1 min-w-0">
      <div className="font-medium text-sm">{name}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{description}</div>
    </div>
    <Badge variant="outline" className="text-xs shrink-0">
      {badge}
    </Badge>
  </div>
);

export default BuildWorkspace;
